import { m as motion } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { FoodArea } from '@/entities/food-area';
import { Place } from '@/entities/place';
import { getCommonPageProps } from '@/shared/api';
import { getEmployees } from '@/shared/api/employees';
import { getFoodAreas } from '@/shared/api/food-areas';
import { getPageDetail } from '@/shared/api/page-detail';
import { getPlaces } from '@/shared/api/places';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AdditionalInfoSection from '@/shared/components/sections/AdditionalInfoSection';
import { AdditionalInfo } from '@/shared/components/sections/AdditionalInfoSection/AdditionalInfoSection';
import FoodAreasList from '@/shared/components/sections/FoodAreasList';
import { FoodAreasListProps } from '@/shared/components/sections/FoodAreasList/FoodAreasList';
import ServiceHeaderSection from '@/shared/components/sections/ServiceHeaderSection';
import ServicesContentHeader from '@/shared/components/sections/ServicesContentHeader';
import { ServicesContentHeaderProps } from '@/shared/components/sections/ServicesContentHeader/ServicesContentHeader';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const ServicesPage = ({
    h1,
    pageTitle,
    titleDescription,
    bodyClass,
    breadcrumbs,
    meta,
    navigation,
    servicesContentHeaderProps,
    foodAreasListProps,
    additionalInfoProps,
    buttonText,
    buttonLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);

    return (
        <DefaultLayout currentPageId={PAGES.FOOD_AREAS} bodyClass={bodyClass} ref={ref}>
            <h1 className="visually-hidden">{h1}</h1>
            <ServiceHeaderSection
                serviceHeaderSectionProps={{
                    breadcrumbs,
                    h1: pageTitle,
                    titleDescription,
                    navigation,
                    buttonText,
                    buttonLink,
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="services-wrapper"
            >
                <ServicesContentHeader servicesContentHeader={servicesContentHeaderProps} />
                <FoodAreasList foodAreasList={foodAreasListProps} />
                <AdditionalInfoSection variant="medium" additionalInfoSectionProps={additionalInfoProps} />
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-food-areas"
                data={generateWebPageJsonLd({
                    name: pageTitle || h1,
                    description: titleDescription || meta.description,
                    pagePath: '/services/food-areas/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ServicesPage;

type ServicesPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        pageTitle: string;
        navigation: AppNavigation;
        titleDescription: string;
        buttonText: string;
        buttonLink: string;
        servicesContentHeaderProps: ServicesContentHeaderProps;
        foodAreasListProps: FoodAreasListProps[];
        additionalInfoProps: AdditionalInfo;
    }
>;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'food-areas' } });

    const [commonPageProps, siteSettings, employeesResponse, foodAreasResponse, placesResponse] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.FOOD_AREAS),
        getSiteSettings({
            params: {
                keys: [
                    'zone_first_screen_title',
                    'zone_first_screen_desription',
                    'zone_first_screen_link',
                    'zone_first_screen_link_text',

                    'zone_zones_title',
                    'zone_zones_link',
                    'zone_zones_link_text',

                    'zone_contact_title',
                    'zone_contact_desription',
                ],
            },
        }),
        getEmployees({
            params: {
                page: pageResponse.data?.id ?? PAGES.FOOD_AREAS,
            },
        }),
        getFoodAreas(),
        getPlaces(),
    ]);

    const foodAreasListProps = foodAreasResponse.data?.map((area: FoodArea) => ({
        ...area,
        place: placesResponse.data?.find((place: Place) => place.id === area.place_id) || null,
    }));

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'services-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Сервисы', href: '/services/' },
            ],
            h1: pageResponse.data?.meta?.title || 'Сервисы',
            pageTitle: siteSettings.data?.zone_first_screen_title || 'Сервисы',
            titleDescription:
                siteSettings.data?.zone_first_screen_desription ||
                'Используйте возможности сервисов, чтобы сделать ваше участие в событиях TNF максимально продуктивным',
            buttonText: siteSettings.data?.zone_first_screen_link_text || 'Служба поддержки',
            buttonLink: siteSettings.data?.zone_first_screen_link || '',
            navigation: [
                {
                    id: 0,
                    title: 'Бронирование гостиниц',
                    href: '/services/hotels/',
                },
                {
                    id: 1,
                    title: 'Аренда транспорта',
                    href: '/services/car-rent/',
                },
                {
                    id: 2,
                    title: 'Аккредитация транспорта',
                    href: '/services/car-accreditation/',
                },
                {
                    id: 3,
                    title: 'Шаттлы',
                    href: '/services/shuttles/',
                },
                {
                    id: 4,
                    title: 'Зоны питания',
                    href: '/services/food-areas/',
                },
            ],
            servicesContentHeaderProps: {
                title: siteSettings.data?.zone_zones_title || 'Зоны питания',
                description: '',
                link: siteSettings.data?.zone_zones_link
                    ? {
                          href: siteSettings.data.zone_zones_link,
                          name: siteSettings.data.zone_zones_link_text || 'Подать заявку',
                      }
                    : null,
            },
            foodAreasListProps: foodAreasListProps || [],

            additionalInfoProps: {
                title: siteSettings.data?.zone_contact_title || 'Дополнительная информация',
                subtitle:
                    siteSettings.data?.zone_contact_desription ||
                    'Заказать услугу и получить дополнительную информацию об условиях можно у оператора Форума в сфере гостеприимства – компании Promo Travel',
                contacts: employeesResponse.data || [],
            },
        } satisfies ServicesPageProps,
        revalidate: 60,
    };
};
